/**
 * Capacity planner. What the Load-test lab HELD, against the growth targets and production's own
 * busiest moment. Staging results stay labelled unverified for production (spec §9). A production
 * floor shows only from a passed, verified production run (G4): a floor on a date, never a ceiling (D3).
 */
import { getCapacity, type CapacityRunView } from '../api';
import { Reading } from '../Reading';
import { central } from '../time';
import { usePoll } from '../usePoll';
import { MetricReading } from './Live';

// Growth targets (greyhive-operations docs/superpowers/plans/load-test-lab-g2-results-g3-proposal.md,
// 2026-10-04): GA4 per-session behaviour scaled to 50k and 100k users a month.
const TARGETS = [
  { name: '50,000 users a month, busiest day', rps: 27 },
  { name: '100,000 users a month, busiest day', rps: 55 },
  { name: 'A viral spike at 100,000 users', rps: 270 },
];

const MIX_LABEL: Record<string, string> = { pageview: 'real-traffic mix', api: 'API-heavy mix' };

function best(runs: CapacityRunView[], mix: string) {
  return runs.filter((r) => r.env === 'staging' && r.mix === mix && r.heldRps !== null)[0];   // newest first
}

export function verifiedProduction(runs: CapacityRunView[]) {
  return runs.filter((r) => r.env === 'production' && r.verified === true && r.heldRps !== null)[0];   // newest first
}

export function ProductionReading({ run, label = 'Production verified, real-traffic mix' }: { run: CapacityRunView | undefined; label?: string }) {
  if (!run) return <Reading label={label} state="unknown" source="Load-test lab" reason="No verified production run yet." />;
  return (
    <Reading label={label} value={String(run.heldRps)} unit="rps" state="verified" environment="production"
      source={`load-test run #${run.id}, ${run.planId}`} observedAt={new Date(run.startedAt)}
      reason={`Held ${run.heldRps} rps on ${central(run.startedAt)}. A floor on that date, not a ceiling: the check stopped at its cap.`} />
  );
}

function HeldReading({ run, mix }: { run: CapacityRunView | undefined; mix: string }) {
  const label = `Staging held, ${MIX_LABEL[mix] ?? mix}`;
  if (!run) return <Reading label={label} state="unknown" source="Load-test lab" reason="No finished run has held a step yet." />;
  return (
    <Reading label={label} value={String(run.heldRps)} unit="rps" state="unverified" environment="staging"
      source={`load-test run #${run.id}, ${run.planId}`} observedAt={new Date(run.startedAt)}
      reason={`Planned up to ${run.maxPlannedRps} rps. Staging, not production: breaking-point tests run here, never on production.`} />
  );
}

export function Capacity() {
  const cap = usePoll(getCapacity, 60_000);
  const runs = cap.data?.runs ?? [];
  const real = best(runs, 'pageview');
  const heavy = best(runs, 'api');
  const prod = verifiedProduction(runs);

  return (
    <>
      <div className="readings">
        <ProductionReading run={prod} />
        <HeldReading run={real} mix="pageview" />
        <HeldReading run={heavy} mix="api" />
        <MetricReading id="capacity.prodPeak5m7d" label="Production busiest 5 min, 7 days (rps)" reduce="single" />
      </div>

      <h3>Growth targets against the real-traffic result</h3>
      <div className="tablewrap"><table>
        <thead><tr><th>Target</th><th>Requests/s</th><th>Production verified</th><th>Staging held</th><th>Headroom (staging)</th></tr></thead>
        <tbody>
          {TARGETS.map((t) => (
            <tr key={t.name}>
              <td>{t.name}</td><td>{t.rps}</td>
              <td>{prod?.heldRps ? (prod.heldRps >= t.rps ? `yes, ${prod.heldRps} rps` : 'not tested this high') : 'not measured'}</td>
              <td>{real ? `${real.heldRps} rps` : 'not measured'}</td>
              <td>{real?.heldRps ? (real.heldRps >= t.rps ? `${(real.heldRps / t.rps).toFixed(1)}×` : 'not reached') : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table></div>

      <h3>Recent runs</h3>
      {runs.length === 0 ? <p className="alert-summary">No finished load-test runs.</p> : (
        <div className="tablewrap"><table>
          <thead><tr><th>Run</th><th>When</th><th>Plan</th><th>Env</th><th>Mix</th><th>Status</th><th>Held / planned</th><th>Stopped by</th></tr></thead>
          <tbody>
            {runs.slice(0, 12).map((r) => (
              <tr key={r.id}>
                <td>#{r.id}</td><td>{central(r.startedAt)}</td><td>{r.planId}</td><td>{r.env}</td><td>{MIX_LABEL[r.mix] ?? r.mix}</td>
                <td>{r.verified ? 'passed, verified' : r.status}</td><td>{r.heldRps ?? 'none'} / {r.maxPlannedRps} rps</td><td>{r.abortReason ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}

      <div className="notice">
        <h3>What each number means</h3>
        <p>
          The production number comes from one approved, read-only check on greyhive.com (G4), capped at
          its plan's top step. It proves production held that rate on that date; it does not say where
          production breaks. Breaking-point tests stay on staging, which runs smaller PeerTube and
          Postgres than production, so staging results are a fair floor, not a limit. The real-traffic
          mix is built from GA4 page shares and recorded page loads; the API-heavy mix sends far more
          ad requests than real visitors do.
        </p>
      </div>
    </>
  );
}
